/**
 * Display text for the Activity tab.
 *
 * Every enum the Activity endpoints send is turned into words here, and an
 * unknown value becomes null (the caller shows a dash or leaves the line
 * out), so a raw enum can never reach the page. Dates are shown in
 * Europe/London, the zone the backend buckets days in.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const LONDON_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * { year, month (1-12), day, hour, minute } in London for an ISO timestamp,
 * or for a YYYY-MM-DD date taken as it is. Null when missing or unparseable.
 */
function londonParts(value) {
  if (!value) return null;
  const text = String(value);
  if (DATE_ONLY.test(text)) {
    const [year, month, day] = text.split('-').map(Number);
    return { year, month, day, hour: 0, minute: 0 };
  }
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return null;
  const parts = {};
  LONDON_PARTS.formatToParts(date).forEach(({ type, value: v }) => { parts[type] = Number(v); });
  return { year: parts.year, month: parts.month, day: parts.day, hour: parts.hour % 24, minute: parts.minute };
}

const pad = (n) => String(n).padStart(2, '0');

/** "YYYY-MM-DD", the London date of a timestamp. */
export function londonDateKey(value) {
  const p = londonParts(value);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}` : null;
}

/** "26 Sep". Empty string when there is no date. */
export function dayMonth(value) {
  const p = londonParts(value);
  return p ? `${p.day} ${MONTHS[p.month - 1]}` : '';
}

/** "26 Sep 2026". */
export function dayMonthYear(value) {
  const p = londonParts(value);
  return p ? `${p.day} ${MONTHS[p.month - 1]} ${p.year}` : '';
}

/** "29 Sep, 19:40". */
export function dayMonthTime(value) {
  const p = londonParts(value);
  return p ? `${p.day} ${MONTHS[p.month - 1]}, ${pad(p.hour)}:${pad(p.minute)}` : '';
}

const dayNumber = (key) => Date.UTC(...key.split('-').map((n, i) => Number(n) - (i === 1 ? 1 : 0))) / 86400000;

/** Whole London days from `value` to `now`: 0 today, 1 yesterday. Null without a date. */
export function daysBetween(value, now) {
  const a = londonDateKey(value);
  const b = londonDateKey(now);
  if (!a || !b) return null;
  return Math.round(dayNumber(b) - dayNumber(a));
}

/** "Today", "Yesterday", "3 days ago", or "Never" without a date. */
export function relativeDay(value, now) {
  const n = daysBetween(value, now);
  if (n == null) return 'Never';
  if (n <= 0) return 'Today';
  if (n === 1) return 'Yesterday';
  return `${n} days ago`;
}

/** The YYYY-MM-DD `days` after (or before, when negative) a YYYY-MM-DD date. */
export function addDays(key, days) {
  const d = new Date((dayNumber(key) + days) * 86400000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** 0 for Monday to 6 for Sunday, for a YYYY-MM-DD date. */
export function weekdayIndex(key) {
  return (new Date(dayNumber(key) * 86400000).getUTCDay() + 6) % 7;
}

/** The Monday of the week a YYYY-MM-DD date falls in. */
export function weekStart(key) {
  return addDays(key, -weekdayIndex(key));
}

/** A number without a trailing ".0": 70 -> "70", 55.5 -> "55.5". */
export function trimNumber(value, digits = 1) {
  return String(Number(Number(value).toFixed(digits)));
}

/** A percentage the backend sends as 0-100: "70%". Null for no value. */
export function percent(value) {
  return value == null ? null : `${trimNumber(value)}%`;
}

/**
 * A paper's score, as staff read it. The figure is the correct answers
 * against the paper's questions ("1 / 27", or "1" when the total is not
 * recorded); `detail` is how many were answered and the accuracy over those
 * ("1 answered · 100% of those"), so one right answer never reads as 100%.
 * With nothing answered the detail says so, and the figure is "0 / total",
 * or "—" without a total. `main` is the figure with "correct" after it.
 */
export function paperScore(correct, total, answered) {
  const figure = total != null ? `${correct} / ${total}` : (answered ? String(correct) : '—');
  return {
    figure,
    main: figure === '—' ? figure : `${figure} correct`,
    detail: answered
      ? `${answered} answered · ${percent((correct / answered) * 100)} of those`
      : 'No questions answered',
  };
}

/** A 0-1 ratio as a whole percentage: 0.62 -> "62%". */
export function ratioPercent(value) {
  return value == null ? null : `${Math.round(Number(value) * 100)}%`;
}

/** "1 turn", "3 turns". */
export function plural(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

/** "+4", "−1" (a real minus sign) or "same"; null when there is nothing to compare with. */
export function signedDelta(current, previous) {
  if (previous == null || current == null) return null;
  const diff = current - previous;
  if (diff === 0) return 'same';
  return diff > 0 ? `+${diff}` : `−${Math.abs(diff)}`;
}

/** How many times feedback was asked for, in words. */
export function timesAsked(n) {
  if (!n) return 'not yet';
  if (n === 1) return 'once';
  if (n === 2) return 'twice';
  return `${n} times`;
}

const lookup = (table) => (value) => (Object.prototype.hasOwnProperty.call(table, value) ? table[value] : null);

export const bandLabel = lookup({ strong: 'Strong', adequate: 'Adequate', developing: 'Developing', weak: 'Weak' });

export const unscoredLabel = lookup({
  too_short: 'Not scored: answer too short',
  disagreed: 'Not scored: the two markings disagreed',
});

export const skillTrend = lookup({ up: '▲', down: '▼', steady: 'Steady' });

/** The tick or cross line. A code with no sentence is left out. */
export function decisionLine(card) {
  if (!card || (card.decision !== 'best' && card.decision !== 'not_best')) return null;
  if (card.decision === 'best') return '✓ Chose the best option';
  const reason = [card.decision_reason, card.diverges_at].filter(Boolean).join(' — ');
  return reason ? `✗ Didn't choose the best option: ${reason}` : "✗ Didn't choose the best option";
}

/** Change between the first and last windows. Null when the series has fewer than two cases. */
export function scoringChange(change) {
  if (change == null) return null;
  if (change === 0) return 'Same as the first cases';
  if (change > 0) return `▲ ${change} since the first cases`;
  return `▼ ${Math.abs(change)} since the first cases`;
}

export const GUIDE = 'A guide, not a mark.';
export const NO_FEEDBACK_SCORE = 'No feedback requests yet, so no score. It appears after the student asks for feedback in the application builder.';

/** Shown / Partly / Not yet from the display integer. 55 and 25 are the marks. */
export function moveWords(score) {
  if (score == null || Number.isNaN(Number(score))) return null;
  if (score >= 55) return 'Shown';
  if (score >= 25) return 'Partly';
  return 'Not yet';
}

/** "▲ N since first", "▼ N since first", "same as first", or null for one point. */
export function changeSinceFirst(points) {
  if (!Array.isArray(points) || points.length < 2) return null;
  const first = points[0]?.value;
  const last = points[points.length - 1]?.value;
  if (first == null || last == null) return null;
  const gap = last - first;
  if (gap === 0) return 'same as first';
  if (gap > 0) return `▲ ${gap} since first`;
  return `▼ ${Math.abs(gap)} since first`;
}

export function otherActivePlans(count) {
  if (!count) return null;
  if (count === 1) return '+1 other active plan';
  return `+${count} other active plans`;
}
export const modeLabel = lookup({ timed: 'Timed', untimed: 'Untimed', timed_extra: 'Timed with extra time' });
export const paperKindLabel = lookup({ mock: 'Mock', past: 'Past paper' });
export const caseTypeLabel = lookup({ system: 'System case', human: 'Human case', technical: 'Technical case' });
export const choiceLabel = lookup({ first: 'First choice', insurance: 'Insurance choice', other: 'Choice' });
export const documentTypeLabel = lookup({ personal_statement: 'Personal statement', cv: 'CV', cover_letter: 'Cover letter' });
export const speakerLabel = lookup({ interviewer: 'Interviewer', candidate: 'Student' });
export const evidenceSourceLabel = lookup({ essay: 'from the written answer', tutorial: 'from the tutorial' });
export const attemptKindLabel = lookup({ tutorial: 'Tutorial', discussion: 'Discussion' });

/** A paper review question's outcome: its words and pill tone. */
export const outcomeLabel = lookup({
  correct: { text: 'Correct', tone: 'g' },
  wrong: { text: 'Wrong', tone: 'r' },
  unanswered: { text: 'Not answered', tone: 'o' },
  not_marked: { text: 'Not marked', tone: 'b' },
  not_recorded: { text: 'Answer not recorded', tone: 'a' },
});

/** A paper question's kind, as staff read it. */
export const questionKindLabel = lookup({
  multiple_choice: 'Multiple choice',
  essay_choice_group: 'Essay',
  yes_no_group: 'Yes or no statements',
  most_least_group: 'Most and least appropriate',
});

/** Whole minutes for a duration in seconds: "72 min". Null for no value. */
export function minutesLabel(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return null;
  return `${Math.max(1, Math.round(Number(seconds) / 60))} min`;
}

/** A short duration: "45s", "3m 40s". Null for no value. */
export function clockLabel(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return null;
  const total = Math.max(0, Math.round(Number(seconds)));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return m ? `${m}m ${pad(sec)}s` : `${sec}s`;
}

/** A writing move's state: the symbol shown, and the words read out with it. */
export const moveState = lookup({
  shown: { symbol: '✓', text: 'Shown', tone: 'ok' },
  partly: { symbol: '~', text: 'Partly', tone: 'part' },
  not_yet: { symbol: '✗', text: 'Not yet', tone: 'no' },
});

/** Option letter for a 0-based index: 0 -> "A". */
export const optionLetter = (index) => (Number.isInteger(index) && index >= 0 && index < 26
  ? String.fromCharCode(65 + index) : '');

/** Bar colour class for a score: '' (good), 'mid' or 'lo'. Numbers are always shown beside the bar. */
export function levelTone(value, outOf) {
  if (value == null || !outOf) return '';
  const ratio = value / outOf;
  if (ratio < 0.45) return 'lo';
  if (ratio < 0.7) return 'mid';
  return '';
}

/** The Activity period control, in order. Keys are the backend's. */
export const ACTIVITY_PERIODS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'term', label: 'This term' },
  { value: 'all', label: 'All time' },
];

/** The Activity period to open on, from the dashboard's period ("today" has no Activity equivalent). */
export function initialActivityPeriod(dashboardPeriod) {
  if (dashboardPeriod === 'today') return '7d';
  return ACTIVITY_PERIODS.some((p) => p.value === dashboardPeriod) ? dashboardPeriod : '30d';
}

/** "Last 30 days, compared with the 30 days before." */
export function comparisonLabel(period) {
  switch (period) {
    case '7d': return 'Last 7 days, compared with the 7 days before.';
    case '30d': return 'Last 30 days, compared with the 30 days before.';
    case 'term': return 'This term, compared with the term before.';
    case 'all': return 'All time.';
    default: return '';
  }
}
